import React, { Fragment, ReactElement, useEffect, useState } from "react";
import { useAppSelector, useAppDispatch } from "redux/hooks";
import { Translate } from "react-redux-i18n";
import { Link } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";

import FontAwesomeIcon from "components/shared/FontAwesomeIcon";
import Navigation from "components/appbar/Navigation";
import { checkWalletPermissions, useWindowSize } from "services/utils";
import { toggleBalance } from "redux/auth/authSlice";
import PasswordChangeAndResetForm from "../../profile/PasswordChangeAndResetForm";
import { openDialog } from "redux/dialog/dialogSlice";
import { getWalletRequested } from "redux/wallet/walletSlice";
import RegisterIcon from "../../../assets/images/header/register.svg"
import { LiveChatWidget } from "@livechat/widget-react"
import AccountMenu from "../AccountMenu";
import Settings from "../Settings/Container";
import { openLiveChat } from "redux/config/configSlice";
import { ReactComponent as Logo } from "../../../assets/images/temporary/Live_Chat_icon.svg"
import { COUPON_UPDATE_INTERVAL_TIMING, WALLET_INTERVAL_TIMING } from "../../../constants";
import styles from "./styles.module.scss";
import { getOddsUpdatesRequested } from "redux/coupon/couponSlice";

interface IProps {
  skinLogo?: string;
  height?: string;
}
export default function Header({ skinLogo, height }: IProps): ReactElement {
  const dispatch = useAppDispatch();
  const [width] = useWindowSize();
  const isAuth = useAppSelector((state) => state.auth.isAuth);
  const permissions = useAppSelector((state) => state.auth.permissions);
  const authWallet = useAppSelector((state) => state.auth.authWallet);
  const isBalanceVisible = useAppSelector((state) => state.auth.isBalanceVisible);
  const isRegistration = useAppSelector((state) => state.config.skin?.setting.registration);
  const username = useAppSelector((state) => state.auth.user?.username);
  const searchResults = useAppSelector((state) => state.wallet.searchResults);
  const isLiveChatOpen = useAppSelector((state) => state.config.isLiveChatOpen);
  const livechatSetting = useAppSelector(state => state.config.skin?.setting.livechat)
  const isDesktopLayout = useAppSelector(state => state.layoutToggle.isDesktopLayout)
  const isTransactionsrDialogOpen = useAppSelector(state => state.dialog.isTransactionsrDialogOpen)
  const couponUpdate = useAppSelector(state => state.coupon.bettingConfig?.setting.coupon_update)
  const odds = useAppSelector(state => state.coupon.odds);

  const [accountMenuAnchor, setAccountMenuAnchor] = useState<(EventTarget & HTMLDivElement) | null>(null);
  const isAccountMenuOpen = Boolean(accountMenuAnchor);
  const [isDialogOpen, setOpen] = useState(false);
  const [isRightDrawerOpen, setRightDrawerOpen] = useState(false);

  const setLiveChatOpen = (state: boolean) => {
    dispatch(openLiveChat(state))
  }

  const handleMenuClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setAccountMenuAnchor(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAccountMenuAnchor(null);
  };

  const handleToggle = () => {
    dispatch(toggleBalance(!isBalanceVisible));
  };

  useEffect(() => {
    (!isDesktopLayout && width < 900) && setAccountMenuAnchor(null);
  }, [width, isDesktopLayout]);

  useEffect(() => {
    !isAuth && setRightDrawerOpen(false)
  }, [isAuth])
  useEffect(() => {
    if (!isTransactionsrDialogOpen) {
      const walletInterval = setInterval(() => {
        authWallet &&
          dispatch(
            getWalletRequested({
              action: searchResults ? "update" : "new-update",
              id: authWallet.id
            }));
      }, WALLET_INTERVAL_TIMING);
      return () => {
        clearInterval(walletInterval);
      };
    }
  }, [authWallet, searchResults, isTransactionsrDialogOpen]);

  useEffect(() => {
    if (couponUpdate === 1) {
      const oddsUpdateIntervanl = setInterval(() => {
        odds.length > 0 &&
          dispatch(
            getOddsUpdatesRequested(JSON.stringify({
              odds: odds.map(obj => obj.code)
            })))
      }, COUPON_UPDATE_INTERVAL_TIMING);
      return () => {
        clearInterval(oddsUpdateIntervanl)
      }
    }
  }, [odds, couponUpdate])

  return (
    <Fragment>
      <Box className={styles.container}>
        <AppBar position="sticky" className={styles.largeAppBar}>
          <Toolbar className={styles.toolbarContainer}>
            {(width > 900 || isDesktopLayout) &&
              <Button
                component={Link}
                to={'/home'}
                className={styles.logoContainer}
              >
                <img src={skinLogo} alt="logo" height={height ? height : '45px'} className={styles.logo} />
              </Button>}
            <Navigation
              skinLogo={skinLogo ? skinLogo : undefined}
              isRightDrawerOpen={isRightDrawerOpen}
              setRightDrawerOpen={setRightDrawerOpen}
              setLiveChatOpen={setLiveChatOpen}
            />

            <Box className={styles.loginContainer}>
              {!isAuth ?
                <Box className={styles.loginContent}>
                  {
                    livechatSetting == 1 && (isRegistration == 1) &&
                    <IconButton
                      onClick={() => setLiveChatOpen(true)}
                      className={styles.liveChatButton}
                    >
                      <Logo />
                    </IconButton>
                  }
                  {
                    (isRegistration == 1) &&
                    <IconButton
                      onClick={() => dispatch(openDialog({ type: "register" }))}
                      className={styles.registerIcon}
                    >
                      <img src={RegisterIcon} alt="" width="25px" />
                    </IconButton>
                  }
                  {
                    (isRegistration == 1) &&
                    <Button
                      variant="contained"
                      onClick={() => dispatch(openDialog({ type: "register" }))}
                      className={styles.registerBtn}
                    >
                      <Typography className={styles.registerBtnLabel}>
                        <Translate value="buttons.register" />
                      </Typography>
                    </Button>
                  }

                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => dispatch(openDialog({ type: "login" }))}
                    className={styles.loginButton}
                  >
                    <Typography className={styles.loginBtnLabel}>
                      <Translate value="buttons.login" />
                    </Typography>
                  </Button>
                  <Settings />
                </Box>

                :
                <Box className={styles.userContainer}>
                  <Box className={styles.user}>
                    <Box
                      onClick={(event) => (width >= 1200 || isDesktopLayout) ? handleMenuClick(event) : setRightDrawerOpen(true)}
                      className={styles.usernameContainer}
                    >
                      <FontAwesomeIcon type="fal" name="user" margin={1} />
                      <Typography className={styles.userName}>{username}</Typography>
                    </Box>
                    <Box className={styles.balanceContainer}>
                      {permissions && checkWalletPermissions(permissions, "wallet.network") &&
                        (<IconButton onClick={handleToggle}
                          size='small'
                          className={styles.eyeIcon}
                        >
                          {
                            isBalanceVisible ? <FontAwesomeIcon type="fas" name="eye" />
                              : <FontAwesomeIcon type="fas" name="eye-slash" />
                          }
                        </IconButton>)}
                      {isBalanceVisible &&
                        <Box className={styles.balance}>
                          <Typography className={styles.balanceValue}>
                            {/*Removed balance  if wallet.overdraft permission doesn't exist*/}
                            {permissions &&
                              checkWalletPermissions(permissions, "wallet.overdraft") && <><Translate value="nav.balance" />:&nbsp;</>
                            }
                            {authWallet &&
                              (authWallet.balance / authWallet.currency.divider).toFixed(
                                authWallet.currency.decimal
                              )}{" "}
                            {authWallet && authWallet.currency.symbol}
                          </Typography>
                          {permissions && checkWalletPermissions(permissions, "wallet.overdraft") &&
                            <Typography className={styles.balanceValue}><Translate value="nav.overdraft" />:&nbsp;
                              {authWallet &&
                                authWallet.overdraft.toFixed(2)
                              }{" "}
                              {authWallet && authWallet.currency.symbol}
                            </Typography>
                          }
                        </Box>}

                    </Box>
                  </Box>
                  {
                    livechatSetting == 1 && (isRegistration == 1) &&
                    <IconButton
                      onClick={() => setLiveChatOpen(true)}
                      className={`${styles.liveChatButton} ${styles.loggedInLiveChat}`}
                    >
                      <Logo />
                    </IconButton>
                  }
                  <Settings />
                </Box>
              }
              <AccountMenu
                menu={isAccountMenuOpen}
                setLiveChatOpen={setLiveChatOpen}
                menuAnchor={accountMenuAnchor}
                handleMenuClose={handleMenuClose}
              />
            </Box>
          </Toolbar>
        </AppBar>
      </Box>
      <ResetDialog
        isDialogOpen={isDialogOpen}
        setOpen={setOpen}
        isReset={false}
      />
      {isLiveChatOpen && process.env.REACT_APP_CHAT_LICENSE &&
        <LiveChatWidget
          license={process.env.REACT_APP_CHAT_LICENSE}
          visibility="maximized"
          onVisibilityChanged={(event) => event.visibility === "hidden" && setLiveChatOpen(false)}
        />
      }
    </Fragment >
  );
}

interface ResetDialogProps {
  isDialogOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isReset: boolean;
}
function ResetDialog({
  isDialogOpen,
  setOpen,
  isReset,
}: ResetDialogProps): ReactElement {
  const handleCancel = () => {
    setOpen(false);
  };
  return (
    <Dialog maxWidth="xs" open={isDialogOpen} onClose={handleCancel}>
      <DialogTitle>
        <Translate value="dialog.resetpass" />
      </DialogTitle>
      <DialogContent>
        <Typography className={styles.contentMessage}>
          <Translate value="dialog.resetMessage" />
        </Typography>
        <PasswordChangeAndResetForm isReset={isReset} />
      </DialogContent>
    </Dialog>
  );
}